/**
 * Store-agnostic price normalization for RevenueCat products.
 *
 * The two stores expose subscription pricing in fundamentally different shapes:
 *
 *  - iOS (StoreKit): a flat product with `price` (recurring base) and an optional
 *    `introPrice` / `discounts[]` for the promo period.
 *  - Android (Google Play Billing v6): pricing lives inside `defaultOption` and
 *    `subscriptionOptions[]` as an ordered `pricingPhases[]` array. Per the RC SDK
 *    docs, `product.price` is merely "the price value of defaultOption" (NOT
 *    guaranteed to be the recurring base), `fullPricePhase` is the *last* phase
 *    (the recurring base), and `introPhase` is "the first phase with amountMicros
 *    > 0" — which equals the recurring phase when there is NO real intro offer.
 *
 * Reading `product.price` / `product.introPrice` directly (as the old paywall and
 * shop code did) therefore worked on iOS but broke on Android: the intro offer
 * was never detected, so the paywall fell back to an annual-vs-monthly comparison
 * and the shop showed an inverted discount.
 *
 * `normalizeProductPricing()` collapses both shapes into a single result the
 * paywall and the shop widget's native price map can share:
 *
 *   {
 *     priceFull,             // recurring base price (number) or null
 *     priceFullString,       // localized base price string or null
 *     priceDiscounted,       // intro/offer price if any, else === priceFull
 *     priceDiscountedString, // localized offer price string or null
 *     hasOffer,              // true iff a genuine intro/offer cheaper than base
 *     currencyCode,
 *     fullPeriod,            // Period-like { unit, value, iso8601 } for the base
 *     offerPeriod,           // Period-like for the offer phase, or null
 *     offerCycles,           // billing cycles the offer applies for (>= 1)
 *     offerRecurring,        // true for DISCOUNTED_RECURRING_PAYMENT / multi-cycle
 *   }
 *
 * Invariant: priceDiscounted <= priceFull. The "original" (struck) price always
 * comes from the SAME product's base phase — never from a different package.
 */

const microsToUnits = (micros) =>
  Number.isFinite(micros) ? micros / 1000000 : null;

/**
 * Normalizes a Google Play PricingPhase into a plain price descriptor. Returns
 * null when the phase (or its micro-price) is absent. A free-trial phase yields
 * a valid descriptor with price 0.
 */
const priceFromPhase = (phase) => {
  const price = microsToUnits(phase?.price?.amountMicros);
  if (price == null) return null;

  return {
    price,
    priceString: phase?.price?.formatted || null,
    currencyCode: phase?.price?.currencyCode || null,
    period: phase?.billingPeriod || null,
    cycles: phase?.billingCycleCount || 1,
    recurring: phase?.offerPaymentMode === "DISCOUNTED_RECURRING_PAYMENT",
  };
};

const isoToPeriod = (iso) => {
  const match = String(iso || "").match(
    /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?$/i,
  );
  if (!match) return null;

  const [, years, months, weeks, days] = match;
  if (years) return { unit: "YEAR", value: Number(years), iso8601: iso };
  if (months) return { unit: "MONTH", value: Number(months), iso8601: iso };
  if (weeks) return { unit: "WEEK", value: Number(weeks), iso8601: iso };
  if (days) return { unit: "DAY", value: Number(days), iso8601: iso };
  return null;
};

/**
 * Coerces the various period shapes RC hands us (a Period object on Android, an
 * ISO-8601 string on iOS) into a single { unit, value, iso8601 } form.
 */
const normalizePeriod = (period) => {
  if (!period) return null;
  if (typeof period === "string") return isoToPeriod(period);
  if (period.unit && period.value != null) {
    return {
      unit: period.unit,
      value: Number(period.value),
      iso8601: period.iso8601 || null,
    };
  }
  return isoToPeriod(period.iso8601);
};

const periodFromIntroLike = (intro) =>
  normalizePeriod(intro?.period) ||
  (intro?.periodUnit
    ? {
        unit: intro.periodUnit,
        value: Number(intro.periodNumberOfUnits) || 1,
        iso8601: intro.period || null,
      }
    : null);

/**
 * Detects an intro/offer from the flat StoreKit-style fields (`introPrice`, then
 * `discounts[]`), returning it only when strictly cheaper than `fullPrice`.
 * Shared by the iOS path and used as a fallback on Android when the offer isn't
 * present in the Google Play pricing phases.
 */
const offerFromFlat = (product, fullPrice) => {
  const intro = product?.introPrice;
  if (
    intro &&
    Number.isFinite(intro.price) &&
    (fullPrice == null || intro.price < fullPrice)
  ) {
    return {
      price: intro.price,
      priceString: intro.priceString || null,
      period: periodFromIntroLike(intro),
      cycles: intro.cycles || 1,
      recurring: (intro.cycles || 1) > 1,
    };
  }

  if (Array.isArray(product?.discounts) && product.discounts.length && fullPrice != null) {
    const cheapest = product.discounts
      .filter((d) => Number.isFinite(d?.price) && d.price < fullPrice)
      .sort((a, b) => a.price - b.price)[0];
    if (cheapest) {
      return {
        price: cheapest.price,
        priceString: cheapest.priceString || null,
        period: periodFromIntroLike(cheapest),
        cycles: cheapest.cycles || 1,
        recurring: (cheapest.cycles || 1) > 1,
      };
    }
  }

  return null;
};

const emptyResult = () => ({
  priceFull: null,
  priceFullString: null,
  priceDiscounted: null,
  priceDiscountedString: null,
  hasOffer: false,
  currencyCode: null,
  fullPeriod: null,
  offerPeriod: null,
  offerCycles: 1,
  offerRecurring: false,
});

/**
 * Android path: derive base + offer from Google Play subscription options.
 * `options` is defaultOption first, then any other subscriptionOptions.
 */
const fromSubscriptionOptions = (product, options) => {
  // Base recurring price = the highest fullPricePhase across options. Every
  // option (base plan and each offer) shares the same recurring base as its last
  // phase, so taking the max is a safe way to ignore malformed intro-only phases.
  let full = null;
  for (const opt of options) {
    const fp =
      priceFromPhase(opt?.fullPricePhase) ||
      priceFromPhase(opt?.pricingPhases?.[opt?.pricingPhases?.length - 1]);
    if (fp?.price != null && (full == null || fp.price > full.price)) {
      full = fp;
    }
  }

  if (full?.price == null && Number.isFinite(product?.price)) {
    full = {
      price: product.price,
      priceString: product.priceString || null,
      currencyCode: product.currencyCode || null,
      period: product.subscriptionPeriod || null,
    };
  }

  if (full?.price == null) return null;

  // Offer = the cheapest "entry" phase (the first phase the user is charged, i.e.
  // pricingPhases[0], or the freePhase/introPhase when phases aren't exposed)
  // that is strictly cheaper than the base. A base-plan option's first phase IS
  // the recurring base, so it's naturally rejected by the `< full.price` guard.
  let bestOffer = null;
  for (const opt of options) {
    const phases = Array.isArray(opt?.pricingPhases) ? opt.pricingPhases : [];
    let entry = priceFromPhase(phases[0]);
    if (!entry) entry = priceFromPhase(opt?.freePhase || opt?.introPhase);
    if (!entry || entry.price == null) continue;
    if (entry.price >= full.price) continue;
    if (bestOffer == null || entry.price < bestOffer.price) bestOffer = entry;
  }

  // Fallback: some SDK/store combinations surface a Google Play offer via the
  // flat introPrice/discounts fields rather than in the pricing phases.
  if (bestOffer == null) bestOffer = offerFromFlat(product, full.price);

  const hasOffer = bestOffer != null;
  return {
    priceFull: full.price,
    priceFullString: full.priceString || null,
    priceDiscounted: hasOffer ? bestOffer.price : full.price,
    priceDiscountedString: hasOffer
      ? bestOffer.priceString || null
      : full.priceString || null,
    hasOffer,
    currencyCode:
      full.currencyCode || bestOffer?.currencyCode || product?.currencyCode || null,
    fullPeriod: normalizePeriod(full.period) || normalizePeriod(product?.subscriptionPeriod),
    offerPeriod: hasOffer ? normalizePeriod(bestOffer.period) : null,
    offerCycles: hasOffer ? Number(bestOffer.cycles) || 1 : 1,
    offerRecurring: hasOffer ? Boolean(bestOffer.recurring) : false,
  };
};

/**
 * iOS / fallback path: flat StoreKit product with price + introPrice/discounts.
 */
const fromFlatProduct = (product) => {
  const priceFull = Number.isFinite(product?.price) ? product.price : null;
  const offer = offerFromFlat(product, priceFull);
  const hasOffer = offer != null;
  return {
    priceFull,
    priceFullString: product?.priceString || null,
    priceDiscounted: hasOffer ? offer.price : priceFull,
    priceDiscountedString: hasOffer
      ? offer.priceString || null
      : product?.priceString || null,
    hasOffer,
    currencyCode: product?.currencyCode || null,
    fullPeriod: normalizePeriod(product?.subscriptionPeriod),
    offerPeriod: hasOffer ? offer.period : null,
    offerCycles: hasOffer ? Number(offer.cycles) || 1 : 1,
    offerRecurring: hasOffer ? Boolean(offer.recurring) : false,
  };
};

/**
 * Collapses a RevenueCat store product into a normalized { priceFull,
 * priceDiscounted, ... } descriptor, transparently handling both StoreKit and
 * Google Play multi-phase pricing. See the module header for the full contract.
 */
export function normalizeProductPricing(product, { introEligible = true } = {}) {
  if (!product) return emptyResult();

  // Prefer Google Play's structured options when present. `defaultOption` first
  // so it wins ties; then every other subscriptionOption for offer discovery.
  const options = [];
  if (product.defaultOption) options.push(product.defaultOption);
  if (Array.isArray(product.subscriptionOptions)) {
    for (const opt of product.subscriptionOptions) {
      if (opt && opt !== product.defaultOption) options.push(opt);
    }
  }

  let result = null;
  if (options.length) result = fromSubscriptionOptions(product, options);
  if (!result) result = fromFlatProduct(product);

  // Per-user intro-offer eligibility. RevenueCat exposes an intro price whenever
  // one is CONFIGURED for the product, regardless of whether THIS user can still
  // redeem it. When the caller has determined the user is ineligible (e.g. a
  // returning subscriber who already used the intro), collapse to the standard
  // price so we never advertise an offer the user won't actually receive.
  if (introEligible === false && result?.hasOffer) {
    return {
      ...result,
      priceDiscounted: result.priceFull,
      priceDiscountedString: result.priceFullString,
      hasOffer: false,
      offerPeriod: null,
      offerCycles: 1,
      offerRecurring: false,
    };
  }

  return result;
}

// Exported for unit tests / paywall period math.
export { normalizePeriod, priceFromPhase };
